import { z } from 'zod';

/* ─────────── Реестр писем (ДК-01, ДК-02, раздел 6.15) ─────────── */

const DateStr = z.iso.date();

export const LETTER_DIRECTIONS = { in: 'Входящее', out: 'Исходящее' } as const;
export type LetterDirection = keyof typeof LETTER_DIRECTIONS;

export const LETTER_STATUSES = {
  registered: 'Зарегистрировано',
  in_work: 'В работе',
  answered: 'Ответ дан',
  closed: 'Закрыто',
} as const;
export type LetterStatus = keyof typeof LETTER_STATUSES;

/** Письма, по которым ещё что-то нужно сделать */
export const OPEN_LETTER: LetterStatus[] = ['registered', 'in_work'];

export const LETTER_ACTIONS = {
  start: 'Взять в работу',
  answer: 'Ответ дан',
  close: 'Закрыть без ответа',
  reopen: 'Открыть снова',
} as const;
export type LetterAction = keyof typeof LETTER_ACTIONS;

/** Номер без пропусков и повторов внутри проекта отдельно для входящих и исходящих (ДК-02) */
export function letterNumber(direction: LetterDirection, seq: number): string {
  return `${direction === 'in' ? 'ВХ' : 'ИСХ'}-${String(seq).padStart(4, '0')}`;
}

const LetterFields = z.object({
  correspondentId: z.uuid().nullable().optional(),
  /** Корреспондент, которого нет в справочнике контрагентов */
  correspondentName: z.string().trim().max(300).nullable().optional(),
  subject: z.string().trim().min(3).max(500),
  summary: z.string().trim().max(5000).nullable().optional(),
  letterDate: DateStr,
  /** Номер и дата на самом письме (у входящего — отправителя) */
  externalNumber: z.string().trim().max(100).nullable().optional(),
  externalDate: DateStr.nullable().optional(),
  contractId: z.uuid().nullable().optional(),
  remarkId: z.uuid().nullable().optional(),
  changeId: z.uuid().nullable().optional(),
  /** Письмо, на которое это — ответ или продолжение */
  replyToId: z.uuid().nullable().optional(),
  needsReply: z.boolean().optional(),
  replyDue: DateStr.nullable().optional(),
  responsibleId: z.uuid().nullable().optional(),
});

export const LetterInput = LetterFields.extend({
  projectId: z.uuid(),
  direction: z.enum(['in', 'out']),
});
export type LetterInput = z.infer<typeof LetterInput>;

export const LetterPatch = LetterFields.partial();
export type LetterPatch = z.infer<typeof LetterPatch>;

export const LetterActionInput = z.object({
  action: z.enum(['start', 'answer', 'close', 'reopen']),
  /** Для «Ответ дан»: письмо-ответ из реестра */
  answerLetterId: z.uuid().nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type LetterActionInput = z.infer<typeof LetterActionInput>;

export const LetterQuery = z.object({
  projectId: z.uuid().optional(),
  direction: z.enum(['in', 'out']).optional(),
  status: z.enum(['open', 'registered', 'in_work', 'answered', 'closed']).optional(),
  overdue: z.coerce.boolean().optional(),
  mine: z.coerce.boolean().optional(),
  q: z.string().trim().max(100).optional(),
});
export type LetterQuery = z.infer<typeof LetterQuery>;

export interface LetterSummary {
  id: string;
  projectId: string;
  projectCode: string;
  number: string;
  direction: LetterDirection;
  subject: string;
  correspondent: string | null;
  letterDate: string;
  externalNumber: string | null;
  status: LetterStatus;
  needsReply: boolean;
  replyDue: string | null;
  overdue: boolean;
  responsibleName: string | null;
}

export interface LetterDetail extends LetterSummary {
  summary: string | null;
  externalDate: string | null;
  correspondentId: string | null;
  contractId: string | null;
  contractNumber: string | null;
  remarkId: string | null;
  remarkNumber: string | null;
  changeId: string | null;
  changeTitle: string | null;
  replyToId: string | null;
  replyToNumber: string | null;
  answerLetterId: string | null;
  answerLetterNumber: string | null;
  responsibleId: string | null;
  answeredAt: string | null;
  closeNote: string | null;
  /** Какие действия доступны этому человеку */
  actions: LetterAction[];
}
